import {InputDate} from '@/components/commons/input-date/InputDate'
import {ProjectFieldsSchema} from '@/features/projects/formSchemas'
import {ProjectExtended} from '@/features/projects/types'
import {useTranslation} from '@/translations/i18n'
import dayjs from 'dayjs'
import {useController, useFormContext, useWatch} from 'react-hook-form'
import {useOutletContext} from 'react-router-dom'
import {z} from 'zod'

const ProjectStartDateFieldSchema = ProjectFieldsSchema.pick({
    id: true,
    startDate: true,
    expirationDate: true,
    editingOperation: true,
    referringProject: true,
    siarProject: true,
    durationDays: true,
    endDate: true
})
type ProjectStartDateFieldSchemaType = z.infer<typeof ProjectStartDateFieldSchema>

export const ProjectStartDateField = ({isDisabled}: {isDisabled?: boolean}) => {
    const {t} = useTranslation()
    const projectRouteContext = useOutletContext<{project: ProjectExtended}>()
    const {control, setValue} = useFormContext<ProjectStartDateFieldSchemaType>()
    const {field, fieldState} = useController({control, name: 'startDate'})
    const expirationDate = useWatch({control, name: 'expirationDate'})
    const endDate = useWatch({control, name: 'endDate'})
    const durationDays = useWatch({control, name: 'durationDays'})

    const currentEndDate = endDate || projectRouteContext?.project?.endDate

    const handleChange = (date: Date | null) => {
        field.onChange(date)
        if (expirationDate) {
            if (dayjs(date).isAfter(expirationDate)) {
                setValue('expirationDate', new Date(''), {shouldDirty: true})
            } else {
                setValue(
                    'expirationDate',
                    dayjs(date)
                        .add(durationDays || 0, 'day')
                        .toDate(),
                    {shouldDirty: true}
                )
            }
        }
    }

    return (
        <InputDate
            required
            label={t('projects:start_project_date')}
            placeholder="GG/MM/AAAA"
            errorMessage={fieldState.error?.message}
            onChange={handleChange}
            value={field.value}
            disableBeforeDate={currentEndDate ? dayjs(currentEndDate).add(1, 'day').toDate() : undefined}
            fullWidth
            disabled={isDisabled}
        />
    )
}
